// ---------------------------------------------------------------------------
// Why — the longer-form essay behind Pleasant. Linked from the landing page's
// "More about the why" button. Leads with Morgan's framing, then makes the
// argument in her voice (drawn from the About copy), and ends by inviting the
// reader into the app.
// ---------------------------------------------------------------------------

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageMeta } from "@/components/PageMeta";

const Why = () => {
  return (
    <main className="min-h-screen bg-hero text-foreground">
      <PageMeta
        title="The why — Pleasant"
        description="Somewhere along the way, we adopted the logic of the technology we built. Pleasant is a small argument for getting some of it back."
        path="/why"
      />

      <div className="mx-auto w-full max-w-2xl px-6 py-10 sm:py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth mb-10">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <article className="space-y-6 text-foreground/90 leading-relaxed">
          <p className="font-display italic text-2xl sm:text-3xl leading-snug text-foreground">
            Somewhere along the way, we started adopting the logic of the technology we built.
          </p>
          <p className="text-lg leading-relaxed">
            We designed it to make our lives easier — and in many ways, it has. But we lost something along the way.
          </p>

          <hr className="border-border/60 !my-10" />

          <p>
            For two decades, navigation apps have asked one question: <em>what's the fastest way?</em> It is a good
            question. It is not the only one.
          </p>
          <p>
            Answered millions of times a day, that question has quietly funneled us onto the same arterials, bypasses,
            and big-box roads — while the quiet street one block over, with the trees and the old church and the little
            bakery, sits empty.
          </p>
          <p>
            The cost was never really measured in minutes. It is the block you have never walked. The river you cross
            twice a day and never actually look at. Ordinary beauty, waiting one turn away, while the shortest blue line
            on the screen pulls you past it again.
          </p>
          <p>
            We started treating the place we live like an obstacle course to get through, not a world to move through.
            And a life is mostly made of these in-between moments: the commute, the errand, the way home. Judge every one
            of them by speed alone, and you trade away a surprising amount of the day.
          </p>

          <blockquote className="not-prose border-l-2 border-primary/60 pl-5 my-8 font-display italic text-xl leading-snug text-foreground">
            We are not here just to get through the world. We are here to inhabit it.
          </blockquote>

          <p>
            Pleasant is a small argument for getting some of that back. It still gets you where you are going. It just
            does not pretend the only thing that matters is how fast.
          </p>
          <p>
            It scores streets the way a thoughtful local would: for water, for trees, for quiet, for the places worth
            seeing. It is allowed to care about more than seconds.
          </p>
          <p>
            None of this requires you to slow down your life. It just hands back the part of the trip that was always
            yours — the scenery, the calm, the small daily pleasure of a nicer way to go.
          </p>
        </article>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <a
            href="/app"
            className="inline-flex items-center justify-center h-11 px-6 rounded-full bg-pleasant text-primary-foreground text-sm font-medium shadow-glow hover:brightness-110 transition-smooth"
          >
            Find a pleasant route
          </a>
          <Link
            to="/"
            className="inline-flex items-center justify-center h-11 px-6 rounded-full border border-border text-sm font-medium hover:bg-muted transition-smooth"
          >
            Back to home
          </Link>
        </div>
      </div>
    </main>
  );
};

export default Why;
